import { useState, useCallback } from 'react';
import apiClient from '../../../lib/apiClient';
import { selectIndicator } from './useSpendIndicators';

// Top-level drill-in state — a capability or signal can open
// TransactionListModal without first opening its edit modal. Shape:
//   { kind: 'capability' | 'signal', code, name, vendor, product }
export function useZtaDrillIn(selectedDepartment, spendIndicators) {
  const [drillIn, setDrillIn] = useState(null);

  const openCapabilityDrillIn = useCallback((cap) => {
    const ind = selectIndicator(spendIndicators, 'capabilities', cap.id);
    setDrillIn({
      kind: 'capability',
      code: cap.id,
      name: cap.name,
      vendor: ind.topVendor,
      product: ind.topProduct,
    });
  }, [spendIndicators]);

  const openSignalDrillIn = useCallback((signal) => {
    const ind = selectIndicator(spendIndicators, 'signals', signal.id);
    setDrillIn({
      kind: 'signal',
      code: signal.id,
      name: signal.label || signal.name || signal.id,
      vendor: ind.topVendor,
      product: ind.topProduct,
    });
  }, [spendIndicators]);

  // Vendor-level drill-in: stamped next to individual vendor/product
  // references (overlay rows, assigned-vendor text, PDP signal vendor).
  // Routes through the host capability or signal endpoint (so the fetch
  // has a real backend scope) and surfaces vendor/product in the modal
  // header so the reviewer sees the context they clicked from.
  const openVendorDrillIn = useCallback(({ vendor, product, scope, code, name }) => {
    if (!vendor || !scope || !code) return;
    setDrillIn({
      kind: scope,
      code,
      name: name || product || vendor,
      vendor,
      product: product || null,
    });
  }, []);

  // TransactionListModal expects one `fetchTransactions` callback. We route
  // to /capability/ or /signal/ based on what the user clicked.
  const fetchDrillInTransactions = useCallback(async () => {
    if (!drillIn || !selectedDepartment?.department_id) {
      return { transactions: [], count: 0, totalSpend: 0 };
    }
    const deptId = selectedDepartment.department_id;
    const path = drillIn.kind === 'signal'
      ? `/api/zero-trust/departments/${deptId}/signal/${drillIn.code}/transactions`
      : `/api/zero-trust/departments/${deptId}/capability/${drillIn.code}/transactions`;
    try {
      return await apiClient.get(path);
    } catch (err) {
      console.error('Error fetching drill-in transactions:', err);
      return { transactions: [], count: 0, totalSpend: 0 };
    }
  }, [drillIn, selectedDepartment?.department_id]);

  return {
    drillIn,
    setDrillIn,
    openCapabilityDrillIn,
    openSignalDrillIn,
    openVendorDrillIn,
    fetchDrillInTransactions,
  };
}
